import React from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('Error capturado por ErrorBoundary:', error, errorInfo);
  }

  handleReload = () => {
    // Reload current page WITHOUT clearing token (preserves login)
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/dashboard';
  };

  handleClearAndLogin = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      const lang = (typeof window !== 'undefined' && window.localStorage?.getItem('language')) || 'es';
      const msgs = {
        es: { title: 'Algo salió mal', desc: 'Ocurrió un error al cargar esta sección. Puedes intentar recargar o volver al inicio.', reload: 'Recargar', home: 'Ir al Inicio', details: 'Ver detalles', logout: 'Cerrar sesión' },
        en: { title: 'Something went wrong', desc: 'An error occurred loading this section. You can reload or go back home.', reload: 'Reload', home: 'Go Home', details: 'Show details', logout: 'Sign out' },
        pt: { title: 'Algo deu errado', desc: 'Ocorreu um erro ao carregar esta seção. Você pode recarregar ou voltar ao início.', reload: 'Recarregar', home: 'Início', details: 'Ver detalhes', logout: 'Sair' },
        fr: { title: 'Une erreur est survenue', desc: 'Une erreur s\'est produite. Vous pouvez recharger ou retourner à l\'accueil.', reload: 'Recharger', home: 'Accueil', details: 'Voir détails', logout: 'Déconnexion' },
      };
      const m = msgs[lang] || msgs.es;
      const errorMsg = this.state.error?.message || String(this.state.error || '');

      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 max-w-lg w-full text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-[#0F172A] mb-2">{m.title}</h2>
            <p className="text-[#64748B] mb-6">{m.desc}</p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
              <button
                onClick={this.handleReload}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-5 py-3 rounded-lg flex items-center justify-center gap-2 transition font-semibold"
              >
                <RefreshCw className="w-5 h-5" />
                {m.reload}
              </button>
              <button
                onClick={this.handleGoHome}
                className="bg-[#E2E8F0] hover:bg-[#F1F5F9] text-[#0F172A] px-5 py-3 rounded-lg flex items-center justify-center gap-2 transition font-medium"
              >
                <Home className="w-5 h-5" />
                {m.home}
              </button>
            </div>

            <button
              onClick={() => this.setState({ showDetails: !this.state.showDetails })}
              className="text-xs text-slate-500 hover:text-[#475569] inline-flex items-center gap-1"
            >
              <ChevronDown className={`w-3 h-3 transition-transform ${this.state.showDetails ? 'rotate-180' : ''}`} />
              {m.details}
            </button>

            {this.state.showDetails && (
              <div className="mt-3 text-left bg-white rounded-lg p-3 text-xs text-red-700 font-mono overflow-auto max-h-40 border border-red-500/30">
                {errorMsg}
                {this.state.errorInfo?.componentStack && (
                  <div className="mt-2 text-[#64748B] whitespace-pre-wrap">{this.state.errorInfo.componentStack.substring(0, 500)}</div>
                )}
              </div>
            )}

            <button
              onClick={this.handleClearAndLogin}
              className="mt-4 text-xs text-slate-500 hover:text-red-600 underline"
            >
              {m.logout}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
